// Align / distribute for multi-selections (customer request, 2026-08-25).
//
// Reference for alignment:
//   - single object          → the label
//   - multi-selection        → the selection's bounding box
//   - multi-selection + KEY  → the key object. Clicking an object INSIDE an
//                              active selection makes it the key (orange dashed
//                              outline); the others align to it, it never moves.
// Distribute spreads the objects so the GAPS between them are equal, inside
// the span they already occupy (first and last stay put).
import { ActiveSelection, type Canvas, type FabricObject } from 'fabric'
import { getCanvas } from './state'
import { labelPx } from './canvas'

export type AlignMode = 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom'
type Box = { left: number; top: number; width: number; height: number }

let key: FabricObject | null = null

const selectionOf = (c: Canvas): ActiveSelection | null => {
  const a = c.getActiveObject()
  return a && a.type === 'activeselection' ? (a as ActiveSelection) : null
}

export function keyObject(): FabricObject | null {
  const sel = selectionOf(getCanvas())
  return key && sel && sel.getObjects().includes(key) ? key : null
}

/** What the align buttons currently align to. */
export function alignReference(): 'label' | 'selection' | 'key' {
  const c = getCanvas()
  if (!c.getActiveObject()) return 'label'
  if (!selectionOf(c)) return 'label'
  return keyObject() ? 'key' : 'selection'
}

const notify = () => document.dispatchEvent(new CustomEvent('olm:alignref'))

export function initKeyObject(c: Canvas): void {
  c.on('mouse:down', (opt) => {
    const sel = selectionOf(c)
    if (!sel) { if (key) { key = null; notify() } return }
    const pt = c.getScenePoint(opt.e)
    const hit = sel.getObjects().find((o) => o.containsPoint(pt))
    if (hit && hit !== key) { key = hit; notify(); c.requestRenderAll() }
  })
  c.on('selection:cleared', () => { if (key) { key = null; notify() } })
  c.on('selection:updated', () => { if (key && !keyObject()) { key = null; notify() } })
  // Orange dashed outline around the key object (drawn after the objects).
  c.on('after:render', (opt) => {
    const k = keyObject()
    const ctx = (opt as { ctx?: CanvasRenderingContext2D }).ctx
    if (!k || !ctx) return
    const v = c.viewportTransform
    const b = k.getBoundingRect()
    const t = ctx.getTransform()
    const r = t.a === 1 && t.d === 1 ? c.getRetinaScaling() : 1
    ctx.save()
    ctx.scale(r, r)
    ctx.strokeStyle = '#f59e0b'
    ctx.lineWidth = 2
    ctx.setLineDash([5, 3])
    ctx.strokeRect(b.left * v[0] + v[4] - 3, b.top * v[3] + v[5] - 3, b.width * v[0] + 6, b.height * v[3] + 6)
    ctx.restore()
  })
}

function moveTo(o: FabricObject, ref: Box, mode: AlignMode): void {
  const b = o.getBoundingRect()
  switch (mode) {
    case 'left': o.set({ left: (o.left ?? 0) + (ref.left - b.left) }); break
    case 'right': o.set({ left: (o.left ?? 0) + (ref.left + ref.width - (b.left + b.width)) }); break
    case 'centerH': o.set({ left: (o.left ?? 0) + (ref.left + ref.width / 2 - (b.left + b.width / 2)) }); break
    case 'top': o.set({ top: (o.top ?? 0) + (ref.top - b.top) }); break
    case 'bottom': o.set({ top: (o.top ?? 0) + (ref.top + ref.height - (b.top + b.height)) }); break
    case 'centerV': o.set({ top: (o.top ?? 0) + (ref.top + ref.height / 2 - (b.top + b.height / 2)) }); break
  }
  o.setCoords()
}

/** Re-select the same objects (their absolute positions changed while unselected). */
function reselect(c: Canvas, objs: FabricObject[]): void {
  const sel = new ActiveSelection(objs, { canvas: c })
  c.setActiveObject(sel)
  c.fire('object:modified', { target: sel } as never) // history + autosave + panel sync
  c.requestRenderAll()
}

export function alignSelection(mode: AlignMode): void {
  const c = getCanvas()
  const active = c.getActiveObject()
  if (!active) return
  const sel = selectionOf(c)
  if (!sel) {
    const { w, h } = labelPx()
    moveTo(active, { left: 0, top: 0, width: w, height: h }, mode)
    c.fire('object:modified', { target: active } as never)
    c.requestRenderAll()
    return
  }
  const objs = sel.getObjects().slice()
  const k = keyObject()
  const ref: Box = k ? k.getBoundingRect() : sel.getBoundingRect()
  c.discardActiveObject()
  for (const o of objs) if (o !== k) moveTo(o, ref, mode)
  key = k
  reselect(c, objs)
}

export function distributeSelection(axis: 'horizontal' | 'vertical'): void {
  const c = getCanvas()
  const sel = selectionOf(c)
  if (!sel || sel.getObjects().length < 3) return
  const objs = sel.getObjects().slice()
  const k = keyObject()
  c.discardActiveObject()
  const h = axis === 'horizontal'
  const items = objs.map((o) => ({ o, b: o.getBoundingRect() })).sort((a, z) => (h ? a.b.left - z.b.left : a.b.top - z.b.top))
  const first = items[0].b, last = items[items.length - 1].b
  const span = h ? last.left + last.width - first.left : last.top + last.height - first.top
  const total = items.reduce((n, it) => n + (h ? it.b.width : it.b.height), 0)
  const gap = (span - total) / (items.length - 1)
  let cursor = h ? first.left : first.top
  for (const it of items) {
    if (h) it.o.set({ left: (it.o.left ?? 0) + (cursor - it.b.left) }); else it.o.set({ top: (it.o.top ?? 0) + (cursor - it.b.top) })
    it.o.setCoords()
    cursor += (h ? it.b.width : it.b.height) + gap
  }
  key = k
  reselect(c, objs)
}
